import { useEffect, useMemo, useState, startTransition } from "react";
import { Link, NavLink, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "./api";
import { formatDuration, getDraftDurationSeconds } from "../shared/time";
import type {
  AnalyticsSummary,
  Contract,
  ContractHistoryResponse,
  ReviewMode,
  ReviewWithDisplay,
  Solution,
  User,
} from "../shared/types";

function useTicker(enabled: boolean) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const timer = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [enabled]);

  return tick;
}

type EditableEntityProps<T extends object> = {
  title: string;
  subtitle: string;
  items: T[];
  columns: { key: string; label: string; render: (item: T) => string | JSX.Element }[];
  onCreate: (payload: Record<string, string>) => Promise<void>;
  onUpdate: (id: string, payload: Record<string, string | boolean>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  createFields: { key: string; label: string; required?: boolean; placeholder?: string }[];
  editFields: { key: string; label: string; required?: boolean }[];
  getId: (item: T) => string;
  getArchived: (item: T) => boolean;
};

function App() {
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(localStorage.getItem("crtt_selected_user"));
  const [activeReview, setActiveReview] = useState<ReviewWithDisplay | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [contractHistory, setContractHistory] = useState<Record<string, ContractHistoryResponse>>({});

  const selectedUser = users.find((user) => user.id === selectedUserId) ?? null;

  async function refreshCoreData(nextSelectedUserId = selectedUserId) {
    const [userList, contractList, solutionList] = await Promise.all([
      api.listUsers(),
      api.listContracts(),
      api.listSolutions(),
    ]);

    startTransition(() => {
      setUsers(userList);
      setContracts(contractList);
      setSolutions(solutionList);
    });

    if (nextSelectedUserId) {
      setActiveReview(await api.getActiveReview(nextSelectedUserId));
    } else {
      setActiveReview(null);
    }
  }

  async function initialize() {
    setLoading(true);
    setError(null);
    try {
      const status = await api.sessionStatus();
      setUnlocked(status.unlocked);
      const storedUserId = localStorage.getItem("crtt_selected_user");
      const effectiveUserId = status.selectedUserId || storedUserId;
      if (effectiveUserId && !status.selectedUserId) {
        await api.selectUser(effectiveUserId);
      }
      setSelectedUserId(effectiveUserId);
      await refreshCoreData(effectiveUserId);
      setAnalytics(await api.analytics());
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        setUnlocked(false);
        setSelectedUserId(null);
        setActiveReview(null);
        localStorage.removeItem("crtt_selected_user");
      } else {
        setError(caught instanceof Error ? caught.message : "Failed to load app.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void initialize();
  }, []);

  async function guarded<T>(work: () => Promise<T>): Promise<T | undefined> {
    setError(null);
    try {
      return await work();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        setUnlocked(false);
        setSelectedUserId(null);
        setActiveReview(null);
        localStorage.removeItem("crtt_selected_user");
      }
      setError(caught instanceof Error ? caught.message : "Something went wrong.");
      return undefined;
    }
  }

  async function handleUnlock(secretPhrase: string) {
    const result = await guarded(() => api.unlock(secretPhrase));
    if (!result) {
      return;
    }
    setUnlocked(true);
    await refreshCoreData();
    setAnalytics(await api.analytics());
  }

  async function handleSelectUser(userId: string | null) {
    const result = await guarded(() => api.selectUser(userId));
    if (!result) {
      return;
    }
    setSelectedUserId(result.selectedUserId);
    if (result.selectedUserId) {
      localStorage.setItem("crtt_selected_user", result.selectedUserId);
      setActiveReview(await api.getActiveReview(result.selectedUserId));
    } else {
      localStorage.removeItem("crtt_selected_user");
      setActiveReview(null);
    }
  }

  async function handleLock() {
    const result = await guarded(() => api.lock());
    if (!result) {
      return;
    }
    setUnlocked(false);
    setSelectedUserId(null);
    setActiveReview(null);
    localStorage.removeItem("crtt_selected_user");
  }

  async function refreshAnalytics() {
    const next = await guarded(() => api.analytics());
    if (next) {
      setAnalytics(next);
    }
  }

  async function refreshHistory(contractId: string) {
    const next = await guarded(() => api.contractHistory(contractId));
    if (next) {
      setContractHistory((current) => ({ ...current, [contractId]: next }));
    }
  }

  const appShell = unlocked ? (
    <div className="app-shell">
      <aside className="sidebar">
        <div>
          <p className="eyebrow">Internal Tool</p>
          <h1>Contract Review Tracker</h1>
          <p className="sidebar-copy">
            Track manual and software-assisted review time across contracts, people, and solutions.
          </p>
        </div>

        <nav className="nav">
          <NavLink to="/">Review</NavLink>
          <NavLink to="/users">Users</NavLink>
          <NavLink to="/contracts">Contracts</NavLink>
          <NavLink to="/solutions">Solutions</NavLink>
          <NavLink to="/analytics">Analytics</NavLink>
          <NavLink to="/settings">Settings</NavLink>
        </nav>

        <button className="sidebar-lock-button" onClick={() => void handleLock()}>
          Lock App
        </button>
      </aside>

      <main className="content">
        <header className="topbar card">
          <div>
            <p className="eyebrow">Current Reviewer</p>
            <h2>{selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : "Select a reviewer"}</h2>
          </div>

          <label className="select-label">
            Reviewer
            <select value={selectedUserId ?? ""} onChange={(event) => void handleSelectUser(event.target.value || null)}>
              <option value="">Select account</option>
              {users
                .filter((user) => !user.isArchived)
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
            </select>
          </label>
        </header>

        {error ? <div className="alert error">{error}</div> : null}

        <Routes>
          <Route
            path="/"
            element={
              <ReviewPage
                selectedUser={selectedUser}
                contracts={contracts}
                solutions={solutions}
                activeReview={activeReview}
                onRefreshActiveReview={async () => {
                  if (selectedUserId) {
                    setActiveReview(await api.getActiveReview(selectedUserId));
                  }
                }}
                onStart={async (payload) => {
                  const draft = await guarded(() => api.startReview(payload));
                  if (draft) {
                    setActiveReview(draft);
                  }
                }}
                onPause={async (id) => {
                  const draft = await guarded(() => api.pauseReview(id));
                  if (draft) {
                    setActiveReview(draft);
                  }
                }}
                onResume={async (id) => {
                  const draft = await guarded(() => api.resumeReview(id));
                  if (draft) {
                    setActiveReview(draft);
                  }
                }}
                onStop={async (id) => {
                  const draft = await guarded(() => api.stopReview(id));
                  if (draft) {
                    setActiveReview(draft);
                  }
                }}
                onSave={async (id) => {
                  const entry = await guarded(() => api.saveReview(id));
                  if (entry) {
                    setActiveReview(null);
                    await refreshAnalytics();
                    if (entry.contractId) {
                      await refreshHistory(entry.contractId);
                    }
                  }
                }}
                onDiscard={async (id) => {
                  const result = await guarded(() => api.discardReview(id));
                  if (result) {
                    setActiveReview(null);
                  }
                }}
              />
            }
          />
          <Route
            path="/users"
            element={
              <EntityPage<User>
                title="Users"
                subtitle="Manage reviewer accounts while preserving stable IDs for history."
                items={users}
                columns={[
                  { key: "name", label: "Name", render: (item) => `${item.firstName} ${item.lastName}` },
                  { key: "status", label: "Status", render: (item) => <ArchivedBadge archived={item.isArchived} /> },
                ]}
                createFields={[
                  { key: "firstName", label: "First name", required: true, placeholder: "Avery" },
                  { key: "lastName", label: "Last name", required: true, placeholder: "Jordan" },
                ]}
                editFields={[
                  { key: "firstName", label: "First name", required: true },
                  { key: "lastName", label: "Last name", required: true },
                ]}
                getId={(item) => item.id}
                getArchived={(item) => item.isArchived}
                onCreate={async (payload) => {
                  const next = await guarded(() => api.createUser(payload as { firstName: string; lastName: string }));
                  if (next) {
                    setUsers((current) => [...current, next].sort(compareUsers));
                  }
                }}
                onUpdate={async (id, payload) => {
                  const next = await guarded(() =>
                    api.updateUser(id, payload as Partial<{ firstName: string; lastName: string; isArchived: boolean }>),
                  );
                  if (next) {
                    setUsers((current) => current.map((item) => (item.id === id ? next : item)).sort(compareUsers));
                    await refreshAnalytics();
                  }
                }}
                onDelete={async (id) => {
                  const result = await guarded(() => api.deleteUser(id));
                  if (result) {
                    await refreshCoreData();
                    await refreshAnalytics();
                  }
                }}
              />
            }
          />
          <Route
            path="/contracts"
            element={
              <ContractsPage
                contracts={contracts}
                onCreate={async (payload) => {
                  const next = await guarded(() => api.createContract(payload as { contractName: string; clientName?: string | null }));
                  if (next) {
                    setContracts((current) => [...current, next].sort(compareContracts));
                  }
                }}
                onUpdate={async (id, payload) => {
                  const next = await guarded(() =>
                    api.updateContract(id, payload as Partial<{ contractName: string; clientName: string | null; isArchived: boolean }>),
                  );
                  if (next) {
                    setContracts((current) => current.map((item) => (item.id === id ? next : item)).sort(compareContracts));
                    await refreshAnalytics();
                    await refreshHistory(id);
                  }
                }}
                onDelete={async (id) => {
                  const result = await guarded(() => api.deleteContract(id));
                  if (result) {
                    await refreshCoreData();
                    await refreshAnalytics();
                  }
                }}
              />
            }
          />
          <Route
            path="/contracts/:contractId/history"
            element={
              <ContractHistoryPage
                history={contractHistory}
                onLoadHistory={refreshHistory}
                onDeleteReview={async (reviewId, contractId) => {
                  const result = await guarded(() => api.deleteReview(reviewId));
                  if (result) {
                    await refreshAnalytics();
                    await refreshHistory(contractId);
                  }
                }}
              />
            }
          />
          <Route
            path="/solutions"
            element={
              <EntityPage<Solution>
                title="Review Software Solutions"
                subtitle="Maintain the software options used during the evaluation."
                items={solutions}
                columns={[
                  { key: "name", label: "Software", render: (item) => item.softwareName },
                  { key: "status", label: "Status", render: (item) => <ArchivedBadge archived={item.isArchived} /> },
                ]}
                createFields={[{ key: "softwareName", label: "Software name", required: true, placeholder: "Kira" }]}
                editFields={[{ key: "softwareName", label: "Software name", required: true }]}
                getId={(item) => item.id}
                getArchived={(item) => item.isArchived}
                onCreate={async (payload) => {
                  const next = await guarded(() => api.createSolution(payload as { softwareName: string }));
                  if (next) {
                    setSolutions((current) => [...current, next].sort(compareSolutions));
                  }
                }}
                onUpdate={async (id, payload) => {
                  const next = await guarded(() =>
                    api.updateSolution(id, payload as Partial<{ softwareName: string; isArchived: boolean }>),
                  );
                  if (next) {
                    setSolutions((current) => current.map((item) => (item.id === id ? next : item)).sort(compareSolutions));
                    await refreshAnalytics();
                  }
                }}
                onDelete={async (id) => {
                  const result = await guarded(() => api.deleteSolution(id));
                  if (result) {
                    await refreshCoreData();
                    await refreshAnalytics();
                  }
                }}
              />
            }
          />
          <Route path="/analytics" element={<AnalyticsPage analytics={analytics} />} />
          <Route
            path="/settings"
            element={
              <SettingsPage
                onSecretPhraseUpdate={async (secretPhrase) => {
                  const result = await guarded(() => api.updateSecretPhrase(secretPhrase));
                  if (result) {
                    setUnlocked(false);
                    setSelectedUserId(null);
                    setActiveReview(null);
                    localStorage.removeItem("crtt_selected_user");
                  }
                }}
                onExport={async (entity) => {
                  const csv = await guarded(() => api.exportEntity(entity));
                  if (!csv) {
                    return;
                  }
                  downloadTextFile(entity, csv);
                }}
                onImport={async (entity, csvText) => {
                  const result = await guarded(() => api.importEntity(entity, csvText));
                  if (result) {
                    await refreshCoreData();
                  }
                  return result?.imported ?? 0;
                }}
              />
            }
          />
        </Routes>
      </main>
    </div>
  ) : (
    <UnlockScreen onUnlock={handleUnlock} error={error} />
  );

  if (loading) {
    return <div className="loading-screen">Loading application…</div>;
  }

  return <div className="page-frame">{appShell}</div>;
}

function UnlockScreen({ onUnlock, error }: { onUnlock: (secretPhrase: string) => Promise<void>; error: string | null }) {
  const [secretPhrase, setSecretPhrase] = useState("");

  return (
    <div className="unlock-screen">
      <div className="unlock-card">
        <p className="eyebrow">Shared Access</p>
        <h1>Unlock the evaluation workspace</h1>
        <p>
          Enter the shared secret phrase to access time tracking, analytics, and administrative settings.
        </p>

        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void onUnlock(secretPhrase);
          }}
        >
          <label>
            Secret phrase
            <input
              type="password"
              value={secretPhrase}
              onChange={(event) => setSecretPhrase(event.target.value)}
              placeholder="Enter phrase"
            />
          </label>

          <button className="primary-button" type="submit">
            Unlock
          </button>
        </form>

        {error ? <div className="alert error">{error}</div> : null}
      </div>
    </div>
  );
}

function ReviewPage(props: {
  selectedUser: User | null;
  contracts: Contract[];
  solutions: Solution[];
  activeReview: ReviewWithDisplay | null;
  onRefreshActiveReview: () => Promise<void>;
  onStart: (payload: { userId: string; contractId: string; mode: ReviewMode; solutionId?: string | null }) => Promise<void>;
  onPause: (id: string) => Promise<void>;
  onResume: (id: string) => Promise<void>;
  onStop: (id: string) => Promise<void>;
  onSave: (id: string) => Promise<void>;
  onDiscard: (id: string) => Promise<void>;
}) {
  const [contractId, setContractId] = useState("");
  const [mode, setMode] = useState<ReviewMode>("manual");
  const [solutionId, setSolutionId] = useState("");
  const tick = useTicker(Boolean(props.activeReview?.state === "running"));

  useEffect(() => {
    void props.onRefreshActiveReview();
  }, [props.selectedUser?.id]);

  const duration = useMemo(() => {
    if (!props.activeReview) {
      return "00:00:00";
    }
    return formatDuration(getDraftDurationSeconds(props.activeReview));
  }, [props.activeReview, tick]);

  const availableContracts = props.contracts.filter((contract) => !contract.isArchived);
  const availableSolutions = props.solutions.filter((solution) => !solution.isArchived);

  return (
    <div className="page-grid">
      <section className="card">
        <p className="eyebrow">New Review</p>
        <h2>Start a contract review</h2>
        <p className="section-copy">Choose a contract and whether the work is manual or software-assisted.</p>

        {!props.selectedUser ? (
          <div className="empty-state">Select a reviewer in the header before starting a review.</div>
        ) : (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void props.onStart({
                userId: props.selectedUser!.id,
                contractId,
                mode,
                solutionId: mode === "software" ? solutionId : null,
              });
            }}
          >
            <label>
              Contract
              <select value={contractId} onChange={(event) => setContractId(event.target.value)} required>
                <option value="">Select contract</option>
                {availableContracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {contract.contractName}
                    {contract.clientName ? ` · ${contract.clientName}` : ""}
                  </option>
                ))}
              </select>
            </label>

            <div className="segmented-control">
              <button
                type="button"
                className={mode === "manual" ? "is-active" : ""}
                onClick={() => setMode("manual")}
              >
                Manual Review
              </button>
              <button
                type="button"
                className={mode === "software" ? "is-active" : ""}
                onClick={() => setMode("software")}
              >
                Software-Assisted
              </button>
            </div>

            {mode === "software" ? (
              <label>
                Review solution
                <select value={solutionId} onChange={(event) => setSolutionId(event.target.value)} required>
                  <option value="">Select solution</option>
                  {availableSolutions.map((solution) => (
                    <option key={solution.id} value={solution.id}>
                      {solution.softwareName}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <button className="primary-button" type="submit" disabled={Boolean(props.activeReview)}>
              {props.activeReview ? "Complete current draft first" : "Start review"}
            </button>
          </form>
        )}
      </section>

      <section className="card timer-card">
        <p className="eyebrow">Active Timer</p>
        <h2>{props.activeReview?.contract?.contractName || "No active review"}</h2>
        <div className="timer-display">{duration}</div>
        <p className="section-copy">
          {props.activeReview
            ? `${props.activeReview.mode === "manual" ? "Manual review" : props.activeReview.solution?.softwareName || "Software-assisted review"}`
            : "Start a review to begin tracking time."}
        </p>

        {props.activeReview ? (
          <div className="button-row">
            {props.activeReview.state === "running" ? (
              <button className="secondary-button" onClick={() => void props.onPause(props.activeReview!.id)}>
                Pause
              </button>
            ) : null}
            {props.activeReview.state === "paused" ? (
              <button className="secondary-button" onClick={() => void props.onResume(props.activeReview!.id)}>
                Resume
              </button>
            ) : null}
            {props.activeReview.state !== "stopped" ? (
              <button className="primary-button" onClick={() => void props.onStop(props.activeReview!.id)}>
                Stop
              </button>
            ) : null}
            {props.activeReview.state === "stopped" ? (
              <>
                <button className="primary-button" onClick={() => void props.onSave(props.activeReview!.id)}>
                  Save review
                </button>
                <button className="secondary-button" onClick={() => void props.onDiscard(props.activeReview!.id)}>
                  Discard review
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function EntityPage<T extends object>(props: EditableEntityProps<T>) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<Record<string, string | boolean>>({});

  return (
    <div className="stack-large">
      <section className="card">
        <p className="eyebrow">{props.title}</p>
        <h2>{props.subtitle}</h2>
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            void props.onCreate(draft).then(() => setDraft({}));
          }}
        >
          {props.createFields.map((field) => (
            <label key={field.key}>
              {field.label}
              <input
                required={field.required}
                placeholder={field.placeholder}
                value={draft[field.key] || ""}
                onChange={(event) => setDraft((current) => ({ ...current, [field.key]: event.target.value }))}
              />
            </label>
          ))}
          <button className="primary-button" type="submit">
            Add
          </button>
        </form>
      </section>

      <section className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {props.columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
                <th>ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {props.items.map((item) => {
                const id = props.getId(item);
                const isEditing = editingId === id;
                return (
                  <tr key={id}>
                    {props.columns.map((column) => <td key={column.key}>{column.render(item)}</td>)}
                    <td className="mono">{id.slice(0, 8)}</td>
                    <td>
                      {isEditing ? (
                        <div className="stack">
                          {props.editFields.map((field) => (
                            <label key={field.key}>
                              {field.label}
                              <input
                                required={field.required}
                                value={String(editingDraft[field.key] ?? (item as Record<string, unknown>)[field.key] ?? "")}
                                onChange={(event) =>
                                  setEditingDraft((current) => ({ ...current, [field.key]: event.target.value }))
                                }
                              />
                            </label>
                          ))}
                          <div className="button-row tight">
                            <button
                              className="primary-button"
                              onClick={() => {
                                void props.onUpdate(id, editingDraft).then(() => {
                                  setEditingId(null);
                                  setEditingDraft({});
                                });
                              }}
                            >
                              Save
                            </button>
                            <button className="secondary-button" onClick={() => setEditingId(null)}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="button-row tight">
                          <button
                            className="secondary-button"
                            onClick={() => {
                              setEditingId(id);
                              setEditingDraft(
                                Object.fromEntries(
                                  props.editFields.map((field) => [
                                    field.key,
                                    String((item as Record<string, unknown>)[field.key] ?? ""),
                                  ]),
                                ),
                              );
                            }}
                          >
                            Edit
                          </button>
                          <button className="secondary-button" onClick={() => void props.onDelete(id)}>
                            Delete
                          </button>
                          {props.getArchived(item) ? (
                            <button className="secondary-button" onClick={() => void props.onUpdate(id, { isArchived: false })}>
                              Restore
                            </button>
                          ) : (
                            <button className="secondary-button" onClick={() => void props.onUpdate(id, { isArchived: true })}>
                              Archive
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ContractsPage(props: {
  contracts: Contract[];
  onCreate: (payload: Record<string, string>) => Promise<void>;
  onUpdate: (id: string, payload: Record<string, string | boolean>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<Record<string, string>>({});

  return (
    <div className="stack-large">
      <section className="card">
        <p className="eyebrow">Contracts</p>
        <h2>Maintain contract names and client references</h2>
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            void props.onCreate(draft).then(() => setDraft({}));
          }}
        >
          <label>
            Contract name
            <input
              required
              value={draft.contractName || ""}
              onChange={(event) => setDraft((current) => ({ ...current, contractName: event.target.value }))}
            />
          </label>
          <label>
            Client name
            <input
              value={draft.clientName || ""}
              onChange={(event) => setDraft((current) => ({ ...current, clientName: event.target.value }))}
            />
          </label>
          <button className="primary-button" type="submit">
            Add
          </button>
        </form>
      </section>

      <section className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Contract</th>
                <th>Client</th>
                <th>Status</th>
                <th>ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {props.contracts.map((contract) => (
                <tr key={contract.id}>
                  <td>{editingId === contract.id ? <input value={editingDraft.contractName || ""} onChange={(event) => setEditingDraft((current) => ({ ...current, contractName: event.target.value }))} /> : contract.contractName}</td>
                  <td>{editingId === contract.id ? <input value={editingDraft.clientName || ""} onChange={(event) => setEditingDraft((current) => ({ ...current, clientName: event.target.value }))} /> : contract.clientName || "—"}</td>
                  <td><ArchivedBadge archived={contract.isArchived} /></td>
                  <td className="mono">{contract.id.slice(0, 8)}</td>
                  <td>
                    <div className="button-row tight">
                      {editingId === contract.id ? (
                        <>
                          <button
                            className="primary-button"
                            onClick={() => {
                              void props.onUpdate(contract.id, editingDraft).then(() => {
                                setEditingId(null);
                                setEditingDraft({});
                              });
                            }}
                          >
                            Save
                          </button>
                          <button className="secondary-button" onClick={() => setEditingId(null)}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="secondary-button"
                            onClick={() => {
                              setEditingId(contract.id);
                              setEditingDraft({
                                contractName: contract.contractName,
                                clientName: contract.clientName || "",
                              });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="secondary-button"
                            onClick={() => void props.onUpdate(contract.id, { isArchived: !contract.isArchived })}
                          >
                            {contract.isArchived ? "Restore" : "Archive"}
                          </button>
                          <button className="secondary-button" onClick={() => void props.onDelete(contract.id)}>
                            Delete
                          </button>
                          <button className="primary-button" onClick={() => navigate(`/contracts/${contract.id}/history`)}>
                            History
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ContractHistoryPage(props: {
  history: Record<string, ContractHistoryResponse>;
  onLoadHistory: (id: string) => Promise<void>;
  onDeleteReview: (reviewId: string, contractId: string) => Promise<void>;
}) {
  const { contractId } = useParams();

  useEffect(() => {
    if (contractId) {
      void props.onLoadHistory(contractId);
    }
  }, [contractId]);

  const openHistory = contractId ? props.history[contractId] : null;

  return (
    <div className="stack-large">
      <section className="card history-header">
        <div>
          <p className="eyebrow">Contract History</p>
          <h2>{openHistory?.contract?.contractName || "Loading contract history"}</h2>
          <p className="section-copy">Review totals and saved entries for this contract on a dedicated page.</p>
        </div>
        <Link className="secondary-link-button" to="/contracts">
          Back to contracts
        </Link>
      </section>

      {openHistory ? (
        <>
          <section className="metrics three-up">
            <MetricCard label="Total time" value={formatDuration(openHistory.summary.totalSeconds)} />
            <MetricCard label="Manual" value={formatDuration(openHistory.summary.manualSeconds)} />
            <MetricCard label="Software" value={formatDuration(openHistory.summary.softwareSeconds)} />
          </section>

          <section className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Reviewer</th>
                    <th>Mode</th>
                    <th>Solution</th>
                    <th>Duration</th>
                    <th>Ended</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {openHistory.reviews.map((review) => (
                    <tr key={review.id}>
                      <td>{[review.userFirstNameSnapshot, review.userLastNameSnapshot].filter(Boolean).join(" ") || "Unknown"}</td>
                      <td>{review.mode === "manual" ? "Manual" : "Software"}</td>
                      <td>{review.solutionNameSnapshot || "—"}</td>
                      <td>{formatDuration(review.durationSeconds)}</td>
                      <td>{new Date(review.endedAt).toLocaleString()}</td>
                      <td>
                        <button
                          className="secondary-button"
                          onClick={() => void props.onDeleteReview(review.id, openHistory.contract!.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : (
        <section className="card">
          <div className="empty-state">Loading history for this contract.</div>
        </section>
      )}
    </div>
  );
}

function AnalyticsPage({ analytics }: { analytics: AnalyticsSummary | null }) {
  if (!analytics) {
    return <div className="card">Analytics are loading…</div>;
  }

  return (
    <div className="stack-large">
      <section className="metrics three-up">
        <MetricCard label="Total review time" value={formatDuration(analytics.totals.totalSeconds)} />
        <MetricCard label="Manual review time" value={formatDuration(analytics.totals.manualSeconds)} />
        <MetricCard label="Software-assisted time" value={formatDuration(analytics.totals.softwareSeconds)} />
      </section>

      <AnalyticsTable title="By Contract" rows={analytics.byContract} />
      <AnalyticsTable title="By Person" rows={analytics.byUser} />
      <AnalyticsTable title="By Solution" rows={analytics.bySolution} />
    </div>
  );
}

function AnalyticsTable({ title, rows }: { title: string; rows: AnalyticsSummary["byContract"] }) {
  return (
    <section className="card">
      <p className="eyebrow">Analytics</p>
      <h2>{title}</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Label</th>
              <th>Manual</th>
              <th>Software</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${title}-${row.id || row.label}`}>
                <td>
                  {row.label} {row.isArchived ? <span className="inline-badge">Archived</span> : null}
                </td>
                <td>{formatDuration(row.manualSeconds)}</td>
                <td>{formatDuration(row.softwareSeconds)}</td>
                <td>{formatDuration(row.totalSeconds)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SettingsPage(props: {
  onSecretPhraseUpdate: (secretPhrase: string) => Promise<void>;
  onExport: (entity: "users" | "contracts" | "solutions" | "reviews") => Promise<void>;
  onImport: (entity: "users" | "contracts" | "solutions", csvText: string) => Promise<number>;
}) {
  const [secretPhrase, setSecretPhrase] = useState("");
  const [importEntity, setImportEntity] = useState<"users" | "contracts" | "solutions">("users");
  const [importResult, setImportResult] = useState("");
  const [importText, setImportText] = useState("");

  return (
    <div className="stack-large">
      <section className="card">
        <p className="eyebrow">Secret Phrase</p>
        <h2>Rotate shared access</h2>
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            void props.onSecretPhraseUpdate(secretPhrase);
          }}
        >
          <label>
            New secret phrase
            <input
              type="password"
              required
              value={secretPhrase}
              onChange={(event) => setSecretPhrase(event.target.value)}
            />
          </label>
          <button className="primary-button" type="submit">
            Update phrase
          </button>
        </form>
      </section>

      <section className="card">
        <p className="eyebrow">CSV Export</p>
        <h2>Download current data</h2>
        <div className="button-row">
          {(["users", "contracts", "solutions", "reviews"] as const).map((entity) => (
            <button key={entity} className="secondary-button" onClick={() => void props.onExport(entity)}>
              Export {entity}
            </button>
          ))}
        </div>
      </section>

      <section className="card">
        <p className="eyebrow">CSV Import</p>
        <h2>Bootstrap people, contracts, or solutions</h2>
        <label>
          Import target
          <select value={importEntity} onChange={(event) => setImportEntity(event.target.value as typeof importEntity)}>
            <option value="users">Users</option>
            <option value="contracts">Contracts</option>
            <option value="solutions">Solutions</option>
          </select>
        </label>
        <label>
          Paste CSV
          <textarea
            rows={8}
            placeholder="Paste CSV content here"
            value={importText}
            onChange={(event) => setImportText(event.target.value)}
          />
        </label>
        <div className="button-row">
          <button
            className="primary-button"
            onClick={() => {
              if (!importText.trim()) {
                return;
              }
              void props.onImport(importEntity, importText).then((count) => {
                setImportResult(`Imported ${count} ${importEntity}.`);
                setImportText("");
              });
            }}
          >
            Import CSV
          </button>
        </div>
        {importResult ? <div className="alert success">{importResult}</div> : null}
      </section>
    </div>
  );
}

function ArchivedBadge({ archived }: { archived: boolean }) {
  return <span className={archived ? "inline-badge is-archived" : "inline-badge"}>{archived ? "Archived" : "Active"}</span>;
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="card metric-card">
      <p className="eyebrow">{label}</p>
      <h2>{value}</h2>
    </div>
  );
}

function downloadTextFile(entity: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${entity}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function compareUsers(a: User, b: User) {
  return a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName);
}

function compareContracts(a: Contract, b: Contract) {
  return a.contractName.localeCompare(b.contractName);
}

function compareSolutions(a: Solution, b: Solution) {
  return a.softwareName.localeCompare(b.softwareName);
}

export default App;
